import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import moment from "moment";
import { Localize } from "../../../app/lang/langs";

/**
 * How a post's readership built up over time.
 *
 * Cumulative rather than per-day buckets: a post read by five people spread over a week
 * produces an all-but-empty column chart, while the cumulative curve is legible whether it
 * has two events or two hundred and needs no bucket size chosen for it. The curve is a step
 * function because the underlying events are discrete — a read happens at an instant, and
 * drawing a smooth slope between two of them would invent readers who do not exist.
 *
 * Inline SVG rather than a charting dependency: one series, one curve, and the project has
 * no chart library to reuse.
 *
 * Single series, so no legend — the heading says what is plotted. Every value the tooltip
 * shows is also in the viewer list underneath, which is this chart's table view.
 */

// One hue, validated against a white surface for the 3:1 mark contrast the axis and grid
// are deliberately kept below. It is the accent ink already used by the news dialogs.
const SERIES = "#B06000"
const SERIES_WASH = "rgba(176, 96, 0, 0.10)"
const GRID = "#ebe9e6"
const INK = "#3a3632"
const INK_MUTED = "#6b6560"
const SURFACE = "#ffffff"

const PAD = { top: 14, right: 16, bottom: 24, left: 34 }
const HEIGHT = 168

/**
 * Tick values for a count axis: at most 4 evenly spaced gaps, landing on whole readable
 * numbers instead of 3.33.
 *
 * The axis tops out at the next multiple of the step rather than at the exact total, so the
 * gaps stay equal. Appending the raw max as a final tick instead would put two labels a
 * couple of pixels apart whenever the total sits just above a step (11 against a step of 5),
 * and they would overlap. The total is never lost by rounding up — it is on the header
 * chip, in the tooltip, and in the list below.
 */
function countTicks(max) {
    if (max <= 1) return [0, 1]
    const steps = [1, 2, 3, 4, 5, 10, 15, 20, 25, 50, 100, 200, 250, 500, 1000]
    const step = steps.find(s => max / s <= 4) || Math.ceil(max / 4)
    const top = Math.ceil(max / step) * step
    const ticks = []
    for (let value = 0; value <= top; value += step) ticks.push(value)
    return ticks
}

/** A span of minutes reads as clock time; anything longer reads as a date. */
function axisTimeFormat(spanMs) {
    if (spanMs <= 36 * 60 * 60 * 1000) return "HH:mm"
    if (spanMs <= 365 * 24 * 60 * 60 * 1000) return "DD/MM"
    return "MM/YY"
}

export default function NewsViewsChart({ viewers, createdAt }) {

    const wrapperRef = useRef(null)
    const [width, setWidth] = useState(0)
    const [cursor, setCursor] = useState(null)

    // The dialog is resizable by the viewport alone, but the chart is laid out in real
    // pixels rather than a scaled viewBox: scaling an SVG stretches its 2px stroke and its
    // labels along with the geometry, which is exactly the chrome that must stay fixed.
    useLayoutEffect(() => {
        const element = wrapperRef.current
        if (!element) return
        const measure = () => setWidth(element.clientWidth)
        measure()
        if (typeof ResizeObserver === "undefined") return
        const observer = new ResizeObserver(measure)
        observer.observe(element)
        return () => observer.disconnect()
    }, [])

    // One point per read, ordered in time and carrying its running total. Rows with an
    // unparseable `seen` are dropped rather than pinned to the epoch.
    const points = useMemo(() => {
        return (viewers || [])
            .map(viewer => ({ time: new Date(viewer.seen).getTime(), name: viewer.collab_name }))
            .filter(point => Number.isFinite(point.time))
            .sort((a, b) => a.time - b.time)
            .map((point, index) => ({ ...point, count: index + 1 }))
    }, [viewers])

    const geometry = useMemo(() => {
        if (!width || !points.length) return null

        const plotWidth = Math.max(width - PAD.left - PAD.right, 10)
        const plotHeight = HEIGHT - PAD.top - PAD.bottom

        // The curve starts at the post, not at its first reader: the flat run before the
        // first read is the delay before anyone opened it, which is part of the story.
        const published = Number.isFinite(Number(createdAt)) ? Number(createdAt) : null
        let start = published !== null ? Math.min(published, points[0].time) : points[0].time
        let last = points[points.length - 1].time
        // A single read, or several within the same instant, would collapse the domain to a
        // zero-width line; give it an hour of air on each side instead.
        if (last - start < 60 * 1000) {
            start -= 30 * 60 * 1000
            last += 30 * 60 * 1000
        }
        // The domain runs a little past the last read so the curve ends on a visible flat
        // run and the end marker has room to sit in. Ending exactly on the last event pins
        // the marker to the axis and hides the plateau the post has settled at.
        const end = last + (last - start) * 0.05

        const total = points.length
        const ticks = countTicks(total)
        const maxTick = ticks[ticks.length - 1]

        const x = (time) => PAD.left + ((time - start) / (end - start)) * plotWidth
        const y = (count) => PAD.top + plotHeight - (count / maxTick) * plotHeight

        // Step-after: hold the previous total until the instant of the read, then jump.
        const steps = []
        steps.push(`M ${x(start)} ${y(0)}`)
        let previous = 0
        for (const point of points) {
            steps.push(`L ${x(point.time)} ${y(previous)}`)
            steps.push(`L ${x(point.time)} ${y(point.count)}`)
            previous = point.count
        }
        steps.push(`L ${x(end)} ${y(total)}`)
        const line = steps.join(" ")
        const area = `${line} L ${x(end)} ${y(0)} L ${x(start)} ${y(0)} Z`

        const format = axisTimeFormat(end - start)
        const timeTicks = [start, start + (end - start) / 2, end]

        return { x, y, start, end, total, ticks, line, area, format, timeTicks, plotWidth, plotHeight }
    }, [width, points, createdAt])

    // Snap to the nearest read, so the reader aims at a moment rather than at a 2px line.
    const nearestIndex = (clientX) => {
        const element = wrapperRef.current
        if (!element || !geometry) return null
        const bounds = element.getBoundingClientRect()
        const offsetX = clientX - bounds.left
        let best = 0
        let bestDistance = Infinity
        points.forEach((point, index) => {
            const distance = Math.abs(geometry.x(point.time) - offsetX)
            if (distance < bestDistance) {
                bestDistance = distance
                best = index
            }
        })
        return best
    }

    // Keyboard reaches the same readouts as the pointer.
    const handleKeyDown = (event) => {
        if (!points.length) return
        if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
            event.preventDefault()
            const step = event.key === "ArrowRight" ? 1 : -1
            const current = cursor === null ? (step > 0 ? -1 : points.length) : cursor
            const next = Math.min(Math.max(current + step, 0), points.length - 1)
            setCursor(next)
        }
        if (event.key === "Escape") setCursor(null)
    }

    useEffect(() => { setCursor(null) }, [points])

    if (!points.length) return null

    const active = cursor !== null && geometry ? points[cursor] : null

    return (
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: INK }}>
                {Localize("NEWS_VIEWS_OVER_TIME")}
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", color: INK_MUTED, mb: 0.5 }}>
                {Localize("NEWS_VIEWS_OVER_TIME_HINT")}
            </Typography>
            <Box
                ref={wrapperRef}
                tabIndex={0}
                role="img"
                aria-label={Localize("NEWS_VIEWS_OVER_TIME")}
                onKeyDown={handleKeyDown}
                onBlur={() => setCursor(null)}
                onPointerMove={(event) => setCursor(nearestIndex(event.clientX))}
                onPointerLeave={() => setCursor(null)}
                sx={{
                    position: "relative",
                    width: "100%",
                    height: HEIGHT,
                    outline: "none",
                    "&:focus-visible": {
                        boxShadow: "0 0 0 2px rgba(176, 96, 0, 0.35)",
                        borderRadius: "8px",
                    },
                }}
            >
                {geometry && (
                    <svg width={width} height={HEIGHT} style={{ display: "block" }}>
                        {/* Hairline, solid, one step off the surface: the grid carries the
                            values the endpoint label does not, without competing with it. */}
                        {geometry.ticks.map(tick => (
                            <g key={`tick_${tick}`}>
                                <line
                                    x1={PAD.left}
                                    x2={width - PAD.right}
                                    y1={geometry.y(tick)}
                                    y2={geometry.y(tick)}
                                    stroke={GRID}
                                    strokeWidth={1}
                                />
                                <text
                                    x={PAD.left - 6}
                                    y={geometry.y(tick) + 3}
                                    textAnchor="end"
                                    style={{ fontSize: 10, fill: INK_MUTED, fontVariantNumeric: "tabular-nums" }}
                                >
                                    {tick}
                                </text>
                            </g>
                        ))}

                        {geometry.timeTicks.map((time, index) => (
                            <text
                                key={`time_${index}`}
                                x={geometry.x(time)}
                                y={HEIGHT - 8}
                                textAnchor={index === 0 ? "start" : index === 2 ? "end" : "middle"}
                                style={{ fontSize: 10, fill: INK_MUTED }}
                            >
                                {moment(time).format(geometry.format)}
                            </text>
                        ))}

                        <path d={geometry.area} fill={SERIES_WASH} />
                        <path
                            d={geometry.line}
                            fill="none"
                            stroke={SERIES}
                            strokeWidth={2}
                            strokeLinejoin="round"
                            strokeLinecap="round"
                        />

                        {active && (
                            <line
                                x1={geometry.x(active.time)}
                                x2={geometry.x(active.time)}
                                y1={PAD.top}
                                y2={HEIGHT - PAD.bottom}
                                stroke={SERIES}
                                strokeWidth={1}
                                opacity={0.45}
                            />
                        )}

                        {/* The end marker carries a surface ring so it stays readable where
                            it sits on the line and beside its label. */}
                        <circle
                            cx={geometry.x(points[points.length - 1].time)}
                            cy={geometry.y(geometry.total)}
                            r={4}
                            fill={SERIES}
                            stroke={SURFACE}
                            strokeWidth={2}
                        />
                        {active && (
                            <circle
                                cx={geometry.x(active.time)}
                                cy={geometry.y(active.count)}
                                r={4}
                                fill={SERIES}
                                stroke={SURFACE}
                                strokeWidth={2}
                            />
                        )}
                    </svg>
                )}

                {/* Values lead, labels follow. Flips to the left of the crosshair near the
                    right edge so it never leaves the card. */}
                {active && geometry && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: Math.min(
                                Math.max(geometry.x(active.time) + 8, 0),
                                Math.max(width - 172, 0)
                            ),
                            pointerEvents: "none",
                            backgroundColor: "rgba(255, 255, 255, 0.97)",
                            border: `1px solid ${GRID}`,
                            borderRadius: "8px",
                            boxShadow: "0 4px 14px rgba(0, 0, 0, 0.08)",
                            px: 1,
                            py: 0.5,
                            maxWidth: 164,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Box sx={{ width: 10, height: 2, backgroundColor: SERIES, borderRadius: "1px" }} />
                            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: INK }}>
                                {active.count}
                            </Typography>
                            <Typography sx={{ fontSize: "0.68rem", color: INK_MUTED }}>
                                {active.count === 1
                                    ? Localize("NEWS_VIEWS_COUNT_ONE")
                                    : Localize("NEWS_VIEWS_COUNT_MANY")}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: "0.68rem", color: INK_MUTED }}>
                            {moment(active.time).format("DD/MM/YYYY HH:mm")}
                        </Typography>
                        <Typography sx={{
                            fontSize: "0.7rem",
                            color: INK,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}>
                            {active.name}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    )
}
